<template>
  <div class="work-log-list" v-loading="loading">
    <WelcomeBanner title="工作日志" :subtitle="`总日志：${total} 条`">
      <template #right>
        <el-tag type="success" effect="plain">成功：{{ totalSuccessCount }} 条</el-tag>
        <el-tag type="danger" effect="plain">失败：{{ totalFailCount }} 条</el-tag>
      </template>
    </WelcomeBanner>

    <!-- 筛选区 -->
    <BaseCard class="filter-section">
      <div class="filter-row">
        <!-- 机器人筛选 -->
        <div class="filter-item">
          <span class="filter-label">机器人：</span>
          <el-select
            v-model="filters.robotId"
            placeholder="请选择机器人"
            clearable
            @change="handleFilterChange"
            filterable
          >
            <el-option
              v-for="robot in robotOptions"
              :key="robot.id"
              :label="robot.name"
              :value="robot.id"
            />
          </el-select>
        </div>

        <!-- 时间范围筛选 -->
        <div class="filter-item">
          <span class="filter-label">时间范围：</span>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="handleDateChange"
            align="right"
          />
        </div>

        <!-- 状态筛选 -->
        <div class="filter-item">
          <span class="filter-label">执行状态：</span>
          <el-select
            v-model="filters.status"
            placeholder="全部状态"
            clearable
            @change="handleFilterChange"
          >
            <el-option label="全部状态" value="" />
            <el-option
              v-for="item in statusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>

        <!-- 重置按钮 -->
        <div class="filter-actions">
          <el-button @click="handleReset">重置</el-button>
          <el-button type="primary" @click="loadWorkLogList">查询</el-button>
        </div>
      </div>
    </BaseCard>

    <!-- 加载状态 -->
    <div v-if="!list.length && loading" class="loading-wrapper">
      <span class="loading-text">加载中...</span>
    </div>

    <!-- 空状态 -->
    <div v-else-if="list.length === 0" class="empty-wrapper">
      <el-empty description="暂无工作日志数据" />
    </div>

    <!-- 日志卡片列表 -->
    <BaseCard class="log-card-container" v-else>
      <div
        v-for="log in filteredList"
        :key="log.log_id"
        class="log-card"
        @click="goToDetail(log.log_id)"
      >
        <div class="log-card-header">
          <div class="log-robot">
            <div class="robot-avatar">
              {{ getRobotName(log.robot_id).charAt(0) }}
            </div>
            <div class="robot-info">
              <div class="robot-name">{{ getRobotName(log.robot_id) }}</div>
              <div class="log-time">
                <i class="el-icon-time"></i>
                {{ formatDate(log.create_time) }}
              </div>
            </div>
          </div>
          <div class="log-status">
            <el-tag :type="getStatusTagType(log.status)" effect="dark">
              {{ getStatusText(log.status) }}
            </el-tag>
          </div>
        </div>

        <div class="log-title-row">
          <h4 class="log-title">{{ log.action_desc || `工作日志 #${log.log_id}` }}</h4>
        </div>

        <div class="log-card-body">
          <p class="log-content">{{ log.result_summary }}</p>
          <div v-if="log.project_id" class="log-project">
            <i class="el-icon-folder-opened"></i> 项目：{{ log.project_id }}
          </div>
        </div>

        <div class="log-card-footer">
          <div class="log-stats">
            <span v-if="log.cost_ms" class="stat-item">
              <i class="el-icon-stopwatch"></i>
              耗时：{{ formatDuration(log.cost_ms) }}
            </span>
          </div>
          <div class="log-action">
            <el-button type="text" icon="el-icon-view">查看详情</el-button>
          </div>
        </div>
      </div>
    </BaseCard>

    <!-- 分页加载 -->
    <div v-if="total > 0" class="pagination-wrapper">
      <el-pagination
        background
        layout="total, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { getMyWorkLogList } from '../api/workLog'
import { getRobotList } from '../api/robot'
import BaseCard from '@/components/BaseCard.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'

export default {
  name: 'WorkLogList',
  components: { BaseCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      list: [], // 完整日志列表（当前页）
      robotOptions: [], // 机器人选项
      filters: {
        robotId: '',
        status: ''
      },
      dateRange: [],
      currentPage: 1,
      pageSize: 10,
      total: 0, // 总记录数
      totalSuccessCount: 0, // 成功总数
      totalFailCount: 0, // 失败总数

      // 状态选项 (数字值匹配 API)
      statusOptions: [
        { label: '执行中', value: 1 },
        { label: '成功', value: 2 },
        { label: '失败', value: 3 }
      ],

      // 状态配置
      statusConfig: {
        1: { text: '执行中', tagType: 'warning' },
        2: { text: '成功', tagType: 'success' },
        3: { text: '失败', tagType: 'danger' }
      }
    }
  },
  computed: {
    // 过滤后的列表（前端再按机器人筛选）
    filteredList() {
      let result = this.list
      // 机器人筛选（前端过滤）
      if (this.filters.robotId) {
        result = result.filter(log => log.robot_id === this.filters.robotId)
      }
      return result
    }
  },
  mounted() {
    this.loadRobotOptions()
    this.loadWorkLogList()
  },
  methods: {
    // 加载机器人选项
    async loadRobotOptions() {
      try {
        const data = await getRobotList()
        if (data && data.list) {
          this.robotOptions = data.list.map(robot => ({
            id: robot.robot_id,
            account: robot.robot_account,
            name: robot.robot_name
          }))
        } else if (Array.isArray(data)) {
          this.robotOptions = data.map(robot => ({
            id: robot.robot_id,
            account: robot.robot_account,
            name: robot.robot_name
          }))
        } else {
          this.generateMockRobotOptions()
        }
      } catch (error) {
        console.error('加载机器人选项失败', error)
        this.generateMockRobotOptions()
      }
    },

    // 生成模拟机器人选项 - 不再使用，保留方法避免运行时错误
    // 注意：API 返回的是 UUID 格式的 robot_id，不再生成数字 ID
    generateMockRobotOptions() {
      this.robotOptions = []  // 清空选项，让用户手动选择
    },

    // 加载工作日志列表
    async loadWorkLogList() {
      this.loading = true
      try {
        // 构建查询参数
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
          status: this.filters.status || null
        }

        // 添加时间范围参数
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_time = Math.floor(this.dateRange[0].getTime() / 1000)
          params.end_time = Math.floor(this.dateRange[1].getTime() / 1000)
        }

        // 调用新的统一接口
        const result = await getMyWorkLogList(params)

        // 处理返回数据
        const list = result?.list || []
        this.total = result?.total || 0
        this.totalSuccessCount = result?.success_count || 0
        this.totalFailCount = result?.fail_count || 0

        this.list = list.map(item => ({
          log_id: item.log_id,
          action_desc: item.action_desc || `工作日志 #${item.log_id}`,
          result_summary: item.result_summary || '',
          robot_id: item.robot_id,
          robot_name: item.robot_name || '',
          project_id: item.project_id || '',
          status: item.status || 1,
          create_time: item.create_time ? new Date(item.create_time).getTime() : Date.now(),
          cost_ms: item.cost_ms || 0
        }))
      } catch (error) {
        console.error('加载工作日志失败', error)
        this.$message.error('加载工作日志失败')
        this.list = []
        this.total = 0
        this.totalSuccessCount = 0
        this.totalFailCount = 0
      } finally {
        this.loading = false
      }
    },

    // 筛选条件变化
    handleFilterChange() {
      this.currentPage = 1
      this.loadWorkLogList()
    },

    // 日期变化
    handleDateChange() {
      this.handleFilterChange()
    },

    // 重置筛选
    handleReset() {
      this.filters = {
        robotId: '',
        status: ''
      }
      this.dateRange = []
      this.currentPage = 1
      this.loadWorkLogList()
    },

    // 分页变化
    handlePageChange(page) {
      this.currentPage = page
      this.loadWorkLogList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      return this.statusConfig[status]?.tagType || 'info'
    },

    // 获取状态文本
    getStatusText(status) {
      return this.statusConfig[status]?.text || '未知'
    },

    // 获取机器人名称
    getRobotName(robotId) {
      // 优先使用接口返回的 robot_name
      const log = this.list.find(l => l.robot_id === robotId)
      if (log && log.robot_name) {
        return log.robot_name
      }
      // 回退到从选项中查找
      const robot = this.robotOptions.find(r => r.id === robotId)
      return robot ? robot.name : robotId
    },

    // 格式化日期
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // 格式化耗时
    formatDuration(minutes) {
      if (minutes < 60) {
        return `${minutes}分钟`
      } else {
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return mins > 0 ? `${hours}小时${mins}分钟` : `${hours}小时`
      }
    },

    // 跳转详情页
    goToDetail(id) {
      this.$router.push({ name: 'WorkLogDetail', params: { id } })
    }
  }
}
</script>

<style lang="scss" scoped>
.work-log-list {
  padding: 16px;
  min-height: calc(100vh - 100px);
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
}

.welcome-banner { margin-bottom: 16px }

/* 筛选区 */
.filter-section {
  padding: var(--spacing-md) var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  background-color: var(--bg-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.filter-label {
  font-size: var(--font-size-base);
  color: var(--text-regular);
  white-space: nowrap;
}

.filter-item :deep(.el-select) {
  width: 180px;
}

.filter-item :deep(.el-date-picker) {
  width: 300px;
}

.filter-actions {
  margin-left: auto;
}

/* 卡片容器 */
.log-card-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
}

/* 日志卡片 */
.log-card {
  position: relative;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: all 0.28s ease;
  border: 1px solid rgba(0,0,0,0.04);
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 28px rgba(0,0,0,0.10);
    transform: translateY(-4px) scale(1.01);
    border-color: rgba(99,102,241,0.25);
  }

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    right: -30px;
    width: 120px;
    height: 120px;
    background: linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(139,92,246,0.08) 100%);
    border-radius: 0 50% 0 50%;
    pointer-events: none;
    transform: rotate(15deg);
  }
}

.log-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
}

/* 机器人信息 */
.log-robot {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.robot-avatar {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  box-shadow: 0 6px 14px rgba(99,102,241,0.16);
}

.robot-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.robot-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.log-time {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 日志标题 */
.log-title-row {
  margin-bottom: var(--spacing-sm);
}

.log-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  line-height: 1.4;
}

.log-meta {
  display: flex;
  gap: var(--spacing-lg);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.log-status {
  flex-shrink: 0;
  margin-left: var(--spacing-md);
}

.log-card-body {
  margin-bottom: var(--spacing-sm);
}

.log-content {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #51585f;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.log-project {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.log-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--border-color-lighter);
}

.log-stats {
  display: flex;
  gap: var(--spacing-lg);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-regular);
}

.log-action {
  color: var(--primary-color);
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: var(--spacing-md);
  background-color: var(--bg-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

/* 加载和空状态 */
.loading-wrapper,
.empty-wrapper {
  padding: 60px var(--spacing-lg);
  background-color: var(--bg-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

/* 响应式适配 */
@media screen and (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-item {
    width: 100%;

    :deep(.el-select),
    :deep(.el-date-picker) {
      flex: 1;
      width: auto;
    }
  }

  .filter-actions {
    margin-left: 0;
    display: flex;
    gap: var(--spacing-xs);

    :deep(.el-button) {
      flex: 1;
    }
  }

  .log-card-header {
    flex-direction: column;
  }

  .log-status {
    margin-left: 0;
    margin-top: var(--spacing-xs);
    align-self: flex-start;
  }

  .log-meta {
    flex-direction: column;
    gap: 4px;
  }

  .log-card-footer {
    flex-direction: column;
    gap: var(--spacing-sm);
    align-items: flex-start;
  }

  .log-stats {
    flex-direction: column;
    gap: 4px;
  }
}
</style>